import { useCallback, useEffect, useState } from 'react';
import Lozenge from '@atlaskit/lozenge';
import Textfield from '@atlaskit/textfield';
import Spinner from '@atlaskit/spinner';
import { useFlags } from '@atlaskit/flag';
import PageHeader from 'shared/components/page-header/page-header';
import SharedButton from 'shared/components/buttons/button';
import SharedTable from 'shared/components/table/table';
import { ISharedTableColumn, ISharedTableRow } from 'shared/components/table/table-interface';
import { useDialog } from 'core/providers/DialogProvider';
import DeliveryRequestRoutingApiService from 'core/api/delivery-request-routing/delivery-request-routing-api.service';
import {
  IInHouseProvider,
  IPartner,
  IRequestType,
  IRoutingTable,
} from 'core/api/delivery-request-routing/delivery-request-routing-api-interface';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';
import AddEditRequestTypeDialog from './add-edit-request-type-dialog';
import AddEditPartnerDialog from './add-edit-partner-dialog';

/**
 * The routing table — what happens to each kind of Delivery Request.
 *
 * A lookup table rather than a rules engine: a request type is found by the
 * answer to the form's first question, and the only variation within one is by
 * pick-up site and by time of day. There is no priority ordering, so rows are
 * alphabetical with the catch-all last.
 *
 * The list of types is agreed before build and comes from the form itself, so
 * nothing here adds or removes a row — it only changes how each is handled.
 *
 * Rendered with the shared table, like every other list in the portal, so the
 * columns, loading state and empty state behave the way they do everywhere
 * else. Windows and warnings sit inside the first cell because the shared
 * table has no notion of a detail row, which is the right trade for
 * consistency.
 */

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** A row is a type plus the warnings the backend raised against it. */
type RoutingRow = IRequestType & { warningMessages: string[]; [key: string]: unknown };

/**
 * What the "If we do not do it" column says.
 *
 * Partner names rather than addresses now: the address is the partners table's
 * business, and a name is what someone scanning the column is looking for.
 */
const describeTarget = (
  type: { forwardToPartner?: string; forwardToPartnerBySite?: Record<string, string> },
  partners: IPartner[],
): string | undefined => {
  const nameOf = (uid?: string) => partners.find((partner) => partner.uid === uid)?.name;

  const main = nameOf(type.forwardToPartner);
  const bySite = Object.keys(type.forwardToPartnerBySite ?? {});

  if (main && bySite.length) return `${main}, and ${bySite.length} by pick-up site`;
  if (main) return main;
  if (bySite.length) return `${bySite.length} partner${bySite.length === 1 ? '' : 's'} by pick-up site`;
  return undefined;
};

/**
 * How many partners this type forwards to who have no number for the requester
 * to ring.
 *
 * Forwarding still works without one — this only costs the requester the line
 * telling them who to call to change or cancel, so it is worth saying on the
 * row without dressing it as a broken address.
 */
const targetsMissingPhone = (
  type: { forwardToPartner?: string; forwardToPartnerBySite?: Record<string, string> },
  partners: IPartner[],
): number => {
  // Counted once per partner, not once per place they are named: a supplier
  // used by the type and by three pick-up sites is one missing number, not four.
  const uids = [type.forwardToPartner, ...Object.values(type.forwardToPartnerBySite ?? {})].filter(
    (uid): uid is string => Boolean(uid),
  );

  return uids
    .filter((uid, index) => uids.indexOf(uid) === index)
    .filter((uid) => {
      const partner = partners.find((candidate) => candidate.uid === uid);
      return partner && !partner.phone;
    }).length;
};

const describeSchedule = (window: IRequestType['windows'][number]): string =>
  `${window.days.map((day) => DAY_NAMES[day]).join(', ')} ${window.from}–${window.to}`;

const COLUMNS: ISharedTableColumn[] = [
  { key: 'label', label: 'Request type', templateId: 'requestType', width: 22 },
  { key: 'key', label: 'Matches', templateId: 'matches', width: 18 },
  // One column, because a type does not have a handling — it has a list of
  // rules, and which one applies depends on when the request arrives.
  { key: 'rules', label: 'Rules', templateId: 'rules', width: 30 },
  // Its own column, not a line under each rule: the destination belongs to the
  // type, and repeating it against every rule said it varied by rule when it
  // does not.
  { key: 'forwardTo', label: 'If we do not do it', templateId: 'forwardTo', width: 20 },
  { key: 'actions', label: '', templateId: 'actions', width: 10, isCompact: true },
];

const DeliveryRequestRouting = () => {
  const flags = useFlags();
  const dialog = useDialog();

  const [routing, setRouting] = useState<IRoutingTable>();
  const [error, setError] = useState<string>();
  const [seeding, setSeeding] = useState(false);
  const [provider, setProvider] = useState<IInHouseProvider>({ name: '', phone: '' });
  const [startLabel, setStartLabel] = useState('');
  const [startAddress, setStartAddress] = useState('');
  const [savingProvider, setSavingProvider] = useState(false);

  const load = useCallback(async () => {
    try {
      const loaded = await DeliveryRequestRoutingApiService.get();
      setRouting(loaded);
      // Seeded from what is stored rather than bound straight to it, so a
      // half-typed name is not lost every time the table below reloads.
      setProvider({
        name: loaded.settings?.inHouseProvider?.name ?? '',
        phone: loaded.settings?.inHouseProvider?.phone ?? '',
      });
      setStartLabel(loaded.settings?.runsStartFrom?.label ?? '');
      setStartAddress(loaded.settings?.runsStartFrom?.address ?? '');
      setError(undefined);
    } catch (caught) {
      setError((caught as Error).message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const seedDefaults = async () => {
    setSeeding(true);
    try {
      const response = await DeliveryRequestRoutingApiService.seedDefaults();
      setRouting(response.routing);
      showSuccessFlag('Routing set up', `${response.created} request types added.`, flags);
    } catch (caught) {
      showErrorFlag('Could not set up routing', (caught as Error).message, flags);
    } finally {
      setSeeding(false);
    }
  };

  const saveProvider = async () => {
    setSavingProvider(true);
    try {
      // Both settings go together: the document is replaced rather than merged,
      // so sending one alone would quietly clear the other.
      const response = await DeliveryRequestRoutingApiService.saveSettings({
        // An empty name clears the setting rather than saving a nameless
        // provider, which would put a blank into the requester's email.
        inHouseProvider: provider.name.trim()
          ? { name: provider.name.trim(), phone: provider.phone?.trim() || undefined }
          : undefined,
        // Both halves or neither: a name with no address cannot be routed from,
        // and the backend refuses it rather than storing something unusable.
        runsStartFrom:
          startLabel.trim() && startAddress.trim()
            ? { label: startLabel.trim(), address: startAddress.trim() }
            : undefined,
      });
      setRouting((current) => (current ? { ...current, settings: response.settings } : current));
      showSuccessFlag('Saved', 'Requesters will see these details on anything we do ourselves.', flags);
    } catch (caught) {
      showErrorFlag('Could not save', (caught as Error).message, flags);
    } finally {
      setSavingProvider(false);
    }
  };

  const openPartnerEditor = (partner?: IPartner) =>
    dialog?.openDialog(<AddEditPartnerDialog partner={partner} onSaved={setRouting} />);

  const openEditor = (type: IRequestType) =>
    dialog?.openDialog(
      <AddEditRequestTypeDialog
        type={type}
        sites={routing?.sites ?? []}
        partners={routing?.partners ?? []}
        onSaved={setRouting}
      />,
    );

  if (error) {
    return (
      <>
        <PageHeader title='Delivery request routing' />
        <div data-testid='routing-error' className='mt-6 rounded border border-red-200 bg-red-50 p-4'>
          <p className='body-02 font-semibold text-red-700'>Could not load the routing table</p>
          <p className='body-01 mt-1 text-red-600'>{error}</p>
        </div>
      </>
    );
  }

  if (!routing) {
    return (
      <>
        <PageHeader title='Delivery request routing' />
        <div data-testid='routing-loading' className='mt-10 flex justify-center'>
          <Spinner size={40} />
        </div>
      </>
    );
  }

  const partners = routing.partners ?? [];

  const rows: ISharedTableRow<RoutingRow>[] = routing.types.map((type) => ({
    key: type.uid,
    data: {
      ...type,
      warningMessages: routing.warnings.find((warning) => warning.uid === type.uid)?.messages ?? [],
    },
  }));

  return (
    <>
      <PageHeader
        title='Delivery request routing'
        subtitle='What happens to each kind of request that arrives, and who decides. The list of types comes from the request form itself.'
      />

      <div className='mt-6 overflow-y-auto pb-10'>
        {/* Above the table on purpose. The table says where a request goes when
            we do not do it; this says who we are when we do. They are the two
            halves of the same sentence in the requester's confirmation email,
            and configured apart they would drift apart. */}
        <div data-testid='in-house-provider' className='mb-6 rounded border bg-white p-4 shadow-sm'>
          <p className='body-02 font-semibold'>When we do it ourselves</p>
          <p className='body-01 mt-1 text-gray-500'>
            The name and number requesters see on anything we keep in-house, the way a forwarding address is shown on
            anything we send on. Used in the email confirming their booking, so they know who to ring to change or
            cancel it.
          </p>
          <div className='mt-3 grid grid-cols-1 gap-2 md:grid-cols-3'>
            <Textfield
              value={provider.name}
              onChange={(event) =>
                setProvider((current) => ({ ...current, name: (event.target as HTMLInputElement).value }))
              }
              placeholder='D-Hive'
              testId='provider-name'
            />
            <Textfield
              value={provider.phone ?? ''}
              onChange={(event) =>
                setProvider((current) => ({ ...current, phone: (event.target as HTMLInputElement).value }))
              }
              placeholder='01234 567890'
              testId='provider-phone'
            />
            <div>
              <SharedButton
                onClick={saveProvider}
                type='button'
                appearance='primary'
                label='Save'
                testId='save-provider'
                isLoading={savingProvider}
              />
            </div>
          </div>
          <p className='body-02 mt-5 font-semibold'>Where runs start</p>
          <p className='body-01 mt-1 text-gray-500'>
            Where a driver sets off from when a group of jobs is built into a run &mdash; a depot, a yard, or one of the
            hospitals. Runs finish at the last drop-off rather than coming back here, so the distance shown is the
            distance actually driven.
          </p>
          <div className='mt-2 grid grid-cols-1 gap-2 md:grid-cols-2' data-testid='runs-start-from'>
            <Textfield
              value={startLabel}
              onChange={(event) => setStartLabel((event.target as HTMLInputElement).value)}
              placeholder='Derby Depot'
              testId='start-label'
            />
            <Textfield
              value={startAddress}
              onChange={(event) => setStartAddress((event.target as HTMLInputElement).value)}
              placeholder='Unit 4, Foo Road, Derby DE21 4BB'
              testId='start-address'
            />
          </div>
          {routing.settings?.runsStartFrom?.resolved ? (
            // Shown back so a wrong pin reads as a wrong address rather than as
            // a solver fault later.
            <p data-testid='start-resolved' className='body-01 mt-1 text-gray-500'>
              Found: {routing.settings.runsStartFrom.resolved.formatted_address.join(', ')}
            </p>
          ) : (
            <p data-testid='start-unset' className='body-01 mt-1 text-amber-700'>
              Not set. Runs cannot be built until a driver has somewhere to set off from.
            </p>
          )}

          {!routing.settings?.inHouseProvider && (
            // Not an error: nothing today reads this, and the routing table
            // works without it. It is the requester's email that will be poorer.
            <p data-testid='provider-unset' className='body-01 mt-2 text-amber-700'>
              Not set yet. Until it is, a booking we keep cannot tell the requester who has it or who to ring.
            </p>
          )}
        </div>

        {/* Under the provider block, above the table: partners are the other
            half of "who ends up with this", and the table below only points at
            them. Setting up routing top to bottom now reads in that order. */}
        <div data-testid='partners' className='mb-6 rounded border bg-white p-4 shadow-sm'>
          <div className='flex items-start justify-between gap-4'>
            <div>
              <p className='body-02 font-semibold'>Partners we send work to</p>
              <p className='body-01 mt-1 text-gray-500'>
                Teams and suppliers, entered once here and then chosen against a request type below. Changing a number
                here changes it everywhere it is used.
              </p>
            </div>
            <span className='shrink-0'>
              <SharedButton
                onClick={() => openPartnerEditor()}
                type='button'
                appearance='default'
                label='Add a partner'
                testId='add-partner'
              />
            </span>
          </div>

          {partners.length === 0 ? (
            <p data-testid='partners-empty' className='body-01 mt-3 text-amber-700'>
              None yet. Until there is at least one, no request type can be pointed anywhere, and everything that
              arrives waits for a person.
            </p>
          ) : (
            <div className='mt-3 divide-y border-t'>
              {partners.map((partner) => (
                <div key={partner.uid} data-testid='partner-row' className='flex items-center gap-4 py-2'>
                  <p className='body-02 w-48 shrink-0 font-semibold'>{partner.name}</p>
                  <p className='body-01 flex-1 text-gray-600'>{partner.email}</p>
                  <p className={`body-01 w-40 shrink-0 ${partner.phone ? 'text-gray-600' : 'text-amber-700'}`}>
                    {partner.phone ?? 'No phone number'}
                  </p>
                  <SharedButton
                    onClick={() => openPartnerEditor(partner)}
                    type='button'
                    appearance='subtle'
                    spacing='compact'
                    label='Edit'
                    testId='edit-partner'
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Shown only when something is actually wrong. */}
        {routing.problems.length > 0 && (
          <div data-testid='routing-problems' className='mb-6 rounded border border-red-300 bg-red-50 p-3'>
            <p className='body-02 font-semibold text-red-800'>This table has a problem</p>
            {routing.problems.map((problem) => (
              <p key={problem} className='body-01 mt-1 text-red-700'>
                {problem}
              </p>
            ))}
          </div>
        )}

        {routing.types.length === 0 ? (
          <div data-testid='routing-empty' className='rounded-md bg-white p-8 text-center shadow-md'>
            <p className='headline-05'>No request types yet</p>
            <p className='body-01 mt-2 text-gray-600'>
              Until this table is set up, every request that arrives waits for a person. The starting set covers each
              question the form can ask, and books everything for review — nothing routes itself until you say so.
            </p>
            <div className='mt-5 flex justify-center'>
              <SharedButton
                type='button'
                appearance='primary'
                label='Set up the default request types'
                isLoading={seeding}
                onClick={seedDefaults}
                testId='seed-defaults'
              />
            </div>
          </div>
        ) : (
          <div data-testid='routing-table'>
            <SharedTable<RoutingRow>
              columns={COLUMNS}
              rows={rows}
              customTemplates={[
                {
                  id: 'requestType',
                  template: (row: RoutingRow) => (
                    <div>
                      <div className='flex flex-wrap items-center gap-2'>
                        <p>{row.label}</p>
                        {row.isFallback && (
                          <span data-testid='type-fallback'>
                            <Lozenge appearance='moved'>Catch-all</Lozenge>
                          </span>
                        )}
                        {!row.active && <Lozenge appearance='default'>Switched off</Lozenge>}
                      </div>

                      {row.warningMessages.map((warning) => (
                        <p key={warning} data-testid='type-warning' className='mt-1 text-amber-700'>
                          {warning}
                        </p>
                      ))}
                    </div>
                  ),
                },
                {
                  id: 'matches',
                  // The name and the Q1 answer are different things, and the
                  // starting set gives both the same value. The quotes make a
                  // stray space visible: a key that does not match Q1 exactly
                  // routes nothing, silently.
                  template: (row: RoutingRow) => (
                    <p data-testid='request-type-key'>
                      {row.isFallback ? 'Any unrecognised request type' : `Matches "${row.key}"`}
                    </p>
                  ),
                },
                {
                  id: 'rules',
                  template: (row: RoutingRow) => (
                    <div className='space-y-2'>
                      {/* The rule that always applies, then any scheduled
                          ones — the same order as the editor, so the row and
                          the dialog read the same way round. */}
                      {[
                        {
                          scope: 'All of the time',
                          action: row.defaultHandling.action,
                          oversight: row.defaultHandling.oversight,
                        },
                        ...row.windows.map((window) => ({
                          scope: `${window.label} · ${describeSchedule(window)}`,
                          action: window.action,
                          oversight: window.oversight,
                        })),
                      ].map((rule) => (
                        <div key={rule.scope} data-testid='type-rule'>
                          <p className='text-gray-500'>{rule.scope}</p>
                          <div className='mt-1 flex flex-wrap items-center gap-1'>
                            <Lozenge appearance={rule.action === 'forward' ? 'inprogress' : 'success'}>
                              {rule.action === 'forward' ? 'Forwards' : 'Books in-house'}
                            </Lozenge>
                            <Lozenge appearance={rule.oversight === 'automatic' ? 'removed' : 'default'}>
                              {rule.oversight === 'automatic' ? 'Automatically' : 'Waits for review'}
                            </Lozenge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ),
                },
                {
                  id: 'forwardTo',
                  template: (row: RoutingRow) => {
                    const target = describeTarget(row, partners);
                    const missingPhone = targetsMissingPhone(row, partners);
                    return target ? (
                      <>
                        <p data-testid='type-forward-target'>{target}</p>
                        {missingPhone > 0 && (
                          <p data-testid='type-forward-no-phone' className='text-amber-700'>
                            {missingPhone === 1 ? 'No phone number' : `${missingPhone} partners without a number`}
                          </p>
                        )}
                      </>
                    ) : (
                      // Not an empty cell: with nowhere to send it, a request
                      // of this type can be neither forwarded nor declined,
                      // and the row should say so rather than look finished.
                      <p data-testid='type-forward-target' className='text-red-700'>
                        Nobody set
                      </p>
                    );
                  },
                },
                {
                  id: 'actions',
                  template: (row: RoutingRow) => (
                    <div className='w-full flex justify-end space-x-2'>
                      <SharedButton
                        onClick={() => openEditor(row)}
                        type='button'
                        appearance='subtle'
                        spacing='compact'
                        label='Update'
                        testId='edit-type'
                      />
                    </div>
                  ),
                },
              ]}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default DeliveryRequestRouting;
